'use client'

import { ContextBar } from './context-bar'
import { Menu } from '@highlight/ui'
import { logAmplitudeEvent } from '../../../../utils'
import { memo, useCallback } from 'react'
import { AttachmentMenuItems } from './menu/attachments-menu'
import { FileAttachmentHandler } from './file-attachment-handler'
import { pdfjs } from 'react-pdf'
import { atom, useSetAtom, useAtomValue } from 'jotai'
import { twMerge } from 'tailwind-merge'
import { useOnSidepanelHidden } from '@renderer/overlay/atoms'

// Set the workerSrc to a CDN-hosted version matching the PDF.js version:
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

const contextBarMenuAtom = atom(false)
export const useContextBarMenuOpen = () => useAtomValue(contextBarMenuAtom)
export const useSetContextBarMenuOpen = () => useSetAtom(contextBarMenuAtom)

export const ContextBarWrapper = memo(({ className }: { className?: string }) => {
  const isMenuOpen = useContextBarMenuOpen()
  const setMenuOpen = useSetContextBarMenuOpen()
  useOnSidepanelHidden(useCallback(() => setMenuOpen(false), [setMenuOpen]))
  return (
    <div className={'flex w-full items-center'}>
      <Menu open={isMenuOpen} onOpenChange={setMenuOpen}>
        {/* The whole context bar opens the menu, so it is the trigger rather than a Button. */}
        <Menu.Trigger
          unstyled
          nativeButton={false}
          render={<div />}
          className={twMerge(
            'm-0 flex w-full cursor-pointer items-start justify-between bg-transparent p-0 outline-none transition-colors',
            className,
          )}
          // Opens on click, not Base UI's press: a pill stops its click so its own action (remove,
          // add) doesn't open the menu, and a click in a preview card portaled out of the bar still
          // bubbles here through React, so only clicks inside the bar count.
          onMouseDown={(event) => event.preventBaseUIHandler()}
          onClick={(event) => {
            event.preventBaseUIHandler()
            if (!event.currentTarget.contains(event.target as Node)) {
              return
            }
            if (!isMenuOpen) {
              logAmplitudeEvent('Open Attachments Menu', {
                trigger: 'Context Bar',
              })
            }
            setMenuOpen(!isMenuOpen)
          }}
        >
          <ContextBar />
        </Menu.Trigger>
        <Menu.Content side="top" align="start" sideOffset={8} alignOffset={8} className="w-fit">
          <AttachmentMenuItems />
        </Menu.Content>
      </Menu>
      <FileAttachmentHandler />
    </div>
  )
})
ContextBarWrapper.displayName = 'ContextBarWrapper'
